import axios from "axios";
import { getBackendMainUrl } from "./global.ts";
import { getUserLogged } from "@/utils/axios/userUtils.ts";

const BASE_URL_API = getBackendMainUrl + "/chatIa/chat";
const HEADERS = {
  headers: {
    "content-type": "application/json",
    authorization: localStorage.getItem("authorization"),
  },
};

// Interface pour les messages
export interface ClaudeMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: Date;
}

// Interface pour la conversation
export interface ConversationMeta {
  id: string;
  title: string;
  lastUpdated: Date;
  messageCount: number;
}

// Interface pour les détails complets d'une conversation
export interface ConversationDetails {
  messages: ClaudeMessage[];
  conversationId: string;
  title: string;
}

/**
 * Récupère la liste des conversations
 */
export const getConversationsList = async () => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.get(
      `${BASE_URL_API}/conversations/${userId}`,
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors de la récupération des conversations:", error);
    throw error;
  }
};

/**
 * Crée une nouvelle conversation
 * @param title Titre optionnel pour la nouvelle conversation
 */
export const createNewConversation = async (title?: string) => {
  try {
    const userId = getUserLogged()!._id;
    const data: any = { userId };
    if (title) data.title = title;

    const result = await axios.post(
      `${BASE_URL_API}/conversations`,
      data,
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors de la création de la conversation:", error);
    throw error;
  }
};

/**
 * Renomme une conversation existante
 * @param conversationId ID de la conversation à renommer
 * @param newTitle Nouveau titre
 */
export const renameConversation = async (
  conversationId: string,
  newTitle: string
) => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.patch(
      `${BASE_URL_API}/conversations/${conversationId}`,
      { userId, title: newTitle },
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors du renommage de la conversation:", error);
    throw error;
  }
};

/**
 * Supprime une conversation spécifique
 * @param conversationId ID de la conversation à supprimer
 */
export const deleteConversation = async (conversationId: string) => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.delete(
      `${BASE_URL_API}/conversations/${conversationId}`,
      {
        ...HEADERS,
        data: { userId },
      }
    );
    return result;
  } catch (error) {
    console.error("Erreur lors de la suppression de la conversation:", error);
    throw error;
  }
};

/**
 * Envoie un message à Claude et récupère la réponse
 * @param message Le message à envoyer
 * @param conversationId ID de la conversation (optionnel, si null une nouvelle conversation sera créée)
 */
export const sendMessageToClaude = async (
  message: string,
  conversationId?: string
) => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.post(
      `${BASE_URL_API}`,
      { userId, message, conversationId },
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors de l'envoi du message à Claude:", error);
    throw error;
  }
};

/**
 * Récupère l'historique d'une conversation spécifique
 * @param conversationId ID de la conversation (optionnel, si null la conversation la plus récente sera récupérée)
 */
export const getConversationHistory = async (conversationId?: string) => {
  try {
    const userId = getUserLogged()!._id;
    const url = conversationId
      ? `${BASE_URL_API}/history/${userId}/${conversationId}`
      : `${BASE_URL_API}/history/${userId}`;

    const result = await axios.get(url, HEADERS);
    return result;
  } catch (error) {
    console.error("Erreur lors de la récupération de l'historique:", error);
    throw error;
  }
};

/**
 * Supprime toutes les conversations de l'utilisateur
 */
export const clearAllConversations = async () => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.delete(
      `${BASE_URL_API}/history/${userId}`,
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors de la suppression des conversations:", error);
    throw error;
  }
};

/**
 * Limite la taille d'une conversation
 * @param conversationId ID de la conversation
 * @param maxMessages Nombre maximum de messages à conserver
 */
export const trimConversation = async (
  conversationId: string,
  maxMessages: number = 30
) => {
  try {
    const userId = getUserLogged()!._id;
    const result = await axios.post(
      `${BASE_URL_API}/trim/${userId}/${conversationId}`,
      { maxMessages },
      HEADERS
    );
    return result;
  } catch (error) {
    console.error("Erreur lors du trim de la conversation:", error);
    throw error;
  }
};
